import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ColorPicker, Text } from '@reveni-io/reveni-ui'

import { availableLanguagesForUser } from '@/common/constants.js'
import { getCurrentAvailableLanguage } from '@/configuration/helpers/helpers.js'
import CustomText from '@/configuration/pages/ConfigurationPage/ExtrasConfiguration/CustomText/CustomText.jsx'

import CodeWidget from './CodeWidget/CodeWidget.jsx'
import SizeGuideLink from './SizeGuideLink/SizeGuideLink.jsx'
import useCreatePreview from './WidgetPreview/useCreatePreview/useCreatePreview.js'
import WidgetPreview from './WidgetPreview/WidgetPreview.jsx'
import * as Styles from './styles.js'

const BracketingWidgetConfiguration = ({
  backgroundColor,
  textColor,
  linkColor,
  customTexts,
  onChangeCustomTexts,
  useCustomTitle,
  onChangeUseCustomTitle,
  useCustomContent,
  onChangeUseCustomContent,
  useSizeGuide,
  onChangeUseSizeGuide,
  supportedLanguages,
  onChangeBackgroundColor,
  onChangeTextColor,
  onChangeLinkColor,
  scriptInfo,
}) => {
  const { t, i18n } = useTranslation()
  const currentLanguage = getCurrentAvailableLanguage(supportedLanguages, i18n.language)
  const [language, setLanguage] = useState(currentLanguage)

  const getAvailableLanguages = () => {
    return availableLanguagesForUser.filter(lang => supportedLanguages?.includes(lang))
  }

  useCreatePreview({
    backgroundColor,
    textColor,
    linkColor,
    customTexts,
    language,
    loadScriptContainerId: 'reveni-bracketing-widget',
    widgetContainerId: 'bracketing-widget',
  })

  const handleChangeCustomTitle = (value, lang) => {
    onChangeCustomTexts(texts => ({
      ...texts,
      title: {
        ...texts.title,
        [lang]: value,
      },
    }))
  }

  const handleChangeCustomContent = (value, lang) => {
    onChangeCustomTexts(texts => ({
      ...texts,
      content: {
        ...texts.content,
        [lang]: value,
      },
    }))
  }

  const handleChangeCustomSizeGuide = value => {
    onChangeCustomTexts(texts => ({
      ...texts,
      size_guide_url: value,
    }))
  }

  return (
    <Styles.Container>
      <Text type="titleM">{t('Widget bracketing')}</Text>
      <Text type="bodyMRegular">
        {t(
          'Añade nuestro widget de bracketing a tu carrito y página de pago para reducir las devoluciones causadas por clientes que piden varias tallas del mismo artículo. Mostrará un mensaje cuando se detecte este comportamiento, tranquilizando a los compradores y asegurándoles que, si la talla que reciben no es la adecuada, recibirán la correcta en solo unos días gracias al cambio instantáneo.'
        )}
      </Text>

      <Styles.ColorsContainer>
        <Text type="titleS">{t('Colores del widget')}</Text>
        <Styles.ColorsPickerContainer>
          <ColorPicker
            onChange={onChangeBackgroundColor}
            label={t('Color del fondo')}
            type="colorPreview"
            defaultColor={backgroundColor}
          />
          <ColorPicker
            onChange={onChangeTextColor}
            label={t('Color del texto')}
            type="colorPreview"
            defaultColor={textColor}
          />
          <ColorPicker
            onChange={onChangeLinkColor}
            label={t('Color del link')}
            type="colorPreview"
            defaultColor={linkColor}
          />
        </Styles.ColorsPickerContainer>
      </Styles.ColorsContainer>

      <Styles.DisplayContainer>
        <Text type="titleS">{t('Texto del widget')}</Text>
        <CustomText
          languages={getAvailableLanguages()}
          onChangeCustomText={handleChangeCustomTitle}
          customTexts={customTexts?.title}
          onCheckUseCustomText={e => onChangeUseCustomTitle(e.target.checked)}
          useCustomText={useCustomTitle}
          supportedLanguages={supportedLanguages}
          title={t('Título')}
          switchLabel={t('Usar texto personalizado')}
          textLanguage={language}
          setTextLanguage={setLanguage}
        />
        <CustomText
          languages={getAvailableLanguages()}
          onChangeCustomText={handleChangeCustomContent}
          customTexts={customTexts?.content}
          onCheckUseCustomText={e => onChangeUseCustomContent(e.target.checked)}
          useCustomText={useCustomContent}
          supportedLanguages={supportedLanguages}
          title={t('Cuerpo')}
          switchLabel={t('Usar texto personalizado')}
          textLanguage={language}
          setTextLanguage={setLanguage}
        />
      </Styles.DisplayContainer>

      <Styles.DisplayContainer>
        <Text type="titleS">{t('Guía de tallas')}</Text>
        <SizeGuideLink
          onChange={handleChangeCustomSizeGuide}
          value={customTexts?.size_guide_url}
          onCheckUseSizeGuide={onChangeUseSizeGuide}
          useSizeGuide={useSizeGuide}
        />
      </Styles.DisplayContainer>
      <WidgetPreview
        backgroundColor={backgroundColor}
        textColor={textColor}
        linkColor={linkColor}
        loadScriptContainerId="reveni-preview-breacketing"
        widgetContainerId="reveni-bracketing-widget"
        title={t('Vista previa')}
        language={language}
        customTexts={customTexts}
      />
      <CodeWidget scriptInfo={scriptInfo} />
    </Styles.Container>
  )
}

export default BracketingWidgetConfiguration
